(() => {
  if (customElements.get('trh-hero-carousel')) return;

  class HeroCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      if (this.slides.length < 2) return;
      this.controller = new AbortController();
      const connection = this.controller;
      const options = { signal: this.controller.signal };
      this.tabs = [...this.querySelectorAll('[data-slide-to]')];
      this.play = this.querySelector('[data-play]');
      this.index = 0;
      this.request = (this.request || 0) + 1;
      this.interval = Math.max(5000, Number(this.dataset.interval) || 7000);
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.dataset.autoplay !== 'true' || this.motion.matches;
      this.hovered = false;
      this.visible = false;
      this.ready = false;
      this.transitioning = false;
      this.style.setProperty('--slide-duration', `${this.interval}ms`);
      this.slides.forEach((slide, index) => {
        slide.hidden = false;
        slide.classList.toggle('is-active', index === 0);
        slide.inert = index !== 0;
        slide.setAttribute('aria-hidden', String(index !== 0));
        this.tabs[index].classList.toggle('is-current', index === 0);
        if (index === 0) this.tabs[index].setAttribute('aria-current', 'true');
        else this.tabs[index].removeAttribute('aria-current');
      });
      this.classList.add('is-enhanced');
      this.querySelector('[data-controls]').hidden = false;

      this.querySelector('[data-previous]').addEventListener('click', () => this.select(this.index - 1, true), options);
      this.querySelector('[data-next]').addEventListener('click', () => this.select(this.index + 1, true), options);
      this.tabs.forEach((tab, index) => tab.addEventListener('click', () => this.select(index, true), options));
      this.play.addEventListener('click', () => {
        this.paused = !this.paused;
        this.syncPlayback();
      }, options);
      this.addEventListener('pointerenter', () => {
        if (matchMedia('(hover: hover)').matches) { this.hovered = true; this.syncPlayback(); }
      }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.syncPlayback(); }, options);
      this.addEventListener('focusin', event => {
        if (!event.target.closest('[data-play]')) { this.paused = true; this.syncPlayback(); }
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.select(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
        }
      }, options);
      document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.paused = true;
        this.syncPlayback();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        const slide = event.target.closest('[data-slide]');
        if (slide && this.contains(slide)) this.select(this.slides.indexOf(slide), true);
      }, options);
      document.addEventListener('shopify:section:unload', event => {
        if (event.target.contains(this)) this.disconnectedCallback();
      }, options);
      this.observer = new IntersectionObserver(entries => {
        if (this.controller !== connection) return;
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.2;
        this.syncPlayback();
      }, { threshold: [0, 0.2] });
      this.observer.observe(this);
      this.ensureImage(0).then(() => {
        if (!this.isConnected || this.controller !== connection) return;
        this.ready = true;
        this.syncPlayback();
        this.preloadTimer = setTimeout(() => this.ensureImage(1), 1000);
      });
    }

    async ensureImage(index) {
      const slide = this.slides[index];
      const template = slide.querySelector('[data-hero-template]');
      if (template) {
        template.replaceWith(template.content.cloneNode(true));
        const nextImage = slide.querySelector('img');
        nextImage.loading = 'eager';
        nextImage.fetchPriority = 'low';
      }
      const image = slide.querySelector('img');
      if (!image) return;
      try { await image.decode(); }
      catch {
        // Keep the product copy and links usable if an image cannot be loaded.
        if (!image.complete) await new Promise(resolve => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        });
        if (!image.naturalWidth) image.hidden = true;
      }
    }

    async select(nextIndex, manual = false) {
      if (manual) this.paused = true;
      const next = (nextIndex + this.slides.length) % this.slides.length;
      if (next === this.index) { this.syncPlayback(); return; }
      const request = ++this.request;
      this.transitioning = true;
      this.syncPlayback();
      this.setAttribute('aria-busy', 'true');
      await this.ensureImage(next);
      if (!this.controller || request !== this.request) return;
      this.slides.forEach((slide, index) => {
        const active = index === next;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        this.tabs[index].classList.toggle('is-current', active);
        if (active) this.tabs[index].setAttribute('aria-current', 'true');
        else this.tabs[index].removeAttribute('aria-current');
      });
      this.index = next;
      this.removeAttribute('aria-busy');
      if (manual) this.querySelector('[data-announcement]').textContent = `${next + 1} of ${this.slides.length}: ${this.slides[next].dataset.model}`;
      clearTimeout(this.transitionTimer);
      this.transitionTimer = setTimeout(() => {
        this.transitioning = false;
        this.syncPlayback();
        this.ensureImage((this.index + 1) % this.slides.length);
      }, this.motion.matches ? 0 : 600);
    }

    syncPlayback() {
      clearTimeout(this.timer);
      const running = this.ready && !this.paused && !this.hovered && this.visible && !document.hidden && !this.transitioning;
      this.dataset.playing = String(running);
      if (this.play) {
        this.play.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
        this.play.querySelector('[data-play-icon]').textContent = this.paused ? '▶' : 'Ⅱ';
      }
      if (running) this.timer = setTimeout(() => this.select(this.index + 1), this.interval);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.preloadTimer);
      clearTimeout(this.transitionTimer);
      this.request++;
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  }

  customElements.define('trh-hero-carousel', HeroCarousel);
})();
